import type {
  ConfirmEmailBySlackTokenResponse,
  ConfirmUserEmailChangeResponse,
  CreateUserResponse,
  EmailConfirmResponse,
  GoogleSignInServerAuthResponse,
  InvitationDecodeResponse,
  LoginResponse,
  MicrosoftSignInResponse,
  RequestConfirmationEmailResponse,
  SetGoogleServerAuthTokenResponse,
} from '@otter-ai/recall-client';
import * as recallApi from '@otter-ai/recall-client';

// TODO(OTWEB-8676): Define the constants below in `api-spec` as enum.
export const ValidAuthMethods = {
  AUTH_METHOD_GOOGLE: 'google',
  AUTH_METHOD_MICROSOFT: 'microsoft',
  AUTH_METHOD_APPLE: 'apple',
  AUTH_METHOD_PASSWORD: 'password',
  AUTH_METHOD_SAML: 'saml',
  AUTH_METHOD_SALESFORCE: 'salesforce',
} as const;

// Use session storage to save temporary data before the user logs in.
// Use partial login state from local storage to save data after the user logs in.
export type PartialLoginState = {
  //redirectUrl should not have a leading slash (e.g. u/123)
  redirectUrl?: string;
  redirectQueryParams?: { [key: string]: string };
  isEmailInUse?: boolean; // return true if email is in use and return false if email is not in use
  firstName?: string;
  lastName?: string;
  fullName?: string;
  workspace?: AuthWorkspace;
  sharingToken?: string;
  isOptimizedShareEmailSignup?: boolean;
  slackSignupToken?: string;
  twoFactorSettings?: TwoFactorSettings;
  ssoRequired?: boolean;
  isLogIn?: boolean;
  confirmationEmailSent?: string; // Store the email address that received confirmation email
};

// inviter from response of /invitation_decode
export type Inviter = {
  date_joined: string;
  email: string;
  first_name: string;
  id: number;
  is_staff: boolean;
  is_superuser: boolean;
  last_name: string;
  name: string;
  workspace_id: number;
};

// workspace from response of /invitation_decode and /check_email
export type AuthWorkspace = {
  AuthNRequestID?: string;
  disable_sso_sandbox?: boolean;
  email: string;
  handle: string;
  id: number;
  is_pending_member: boolean;
  name: string;
  saml_url?: string;
  sso_enabled: boolean | null;
  sso_required: boolean;
  two_factor_required?: boolean;
};

export type TwoFactorSettings = {
  device_verified: boolean;
  otp_phone_number: string | null;
  two_factor_required: boolean;
  two_factor_type: 'sms' | 'totp' | null;
};

export type ValidAuthMethod =
  (typeof ValidAuthMethods)[keyof typeof ValidAuthMethods];

export const ValidZoomServiceType = {
  LIVE_NOTES: 'live_notes',
  CLOUD_SYNC: 'cloud_sync',
} as const;

export const ValidZoomServiceTypeValues = Object.values(ValidZoomServiceType);

export type ValidZoomServiceType =
  (typeof ValidZoomServiceType)[keyof typeof ValidZoomServiceType];

export const Security2FAType = {
  AUTHENTICATION_APP: 'totp', // TOTP: Time-based One-Time Password
  SMS: 'sms',
  NONE: 'none',
} as const;
export type Security2FAType =
  (typeof Security2FAType)[keyof typeof Security2FAType];

export const SharingTokenFlowType = {
  CLICK_TO_SIGNUP: 'click_to_signup',
  OPTIMIZED_SHARE_EMAIL_SIGNUP: 'optimized_share_email_signup',
} as const;
export type SharingTokenFlowType =
  (typeof SharingTokenFlowType)[keyof typeof SharingTokenFlowType];

export type Security2FATypeEnabled = Exclude<Security2FAType, 'none'>;

export type GoogleSignInServerAuthType = GoogleSignInServerAuthResponse;

export type MicrosoftSignInType = MicrosoftSignInResponse;

export type SetGoogleServerAuthTokenType = SetGoogleServerAuthTokenResponse;

export type CreateUserType = CreateUserResponse;

export type LoginType = LoginResponse;

export type InvitationDecodeType = InvitationDecodeResponse;

export type RequestConfirmationEmailType = RequestConfirmationEmailResponse;

export type EmailConfirmType = EmailConfirmResponse;

export type ConfirmUserEmailChangeType = ConfirmUserEmailChangeResponse;

export type ConfirmEmailBySlackTokenType = ConfirmEmailBySlackTokenResponse;

// TODO(sleahy): Add a standalone type for UserResponse
export type UserResponse = Exclude<
  Awaited<ReturnType<typeof recallApi.getUserProfile>>['data'],
  undefined
>;
